import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button, useToggle } from '@factorialco/deprecated-design-system'
import { ActionsDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { employeeUser } from 'modules/core/store/collections/users'

import { BecomeModal } from 'modules/authentication/public'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import useShowBecomeHint from 'modules/employees/hooks/useShowBecomeHint'
import type Employee from 'modules/employees/store/models/Employee'
import { employeeJob } from 'modules/job_catalog/store/collections/roles'

import ActivateModal from './ActivateModal'
import BecomeHint from './BecomeHint'
import DisableMfaModal from './DisableMfaModal'
import SetWelcomeSpaceAccessModal from './SetWelcomeSpaceAccessModal'
import UninviteModal from './UninviteModal'
import useOptions from './useOptions'

interface Props {
  employee: Employee
  contractVersions: ContractVersions
}

const Actions = ({ employee, contractVersions }: Props) => {
  const { pathname } = useLocation()
  const [openBecome, setOpenBecome] = React.useState(false)
  const [openDisableMfa, setOpenDisableMfa] = React.useState(false)
  const [openUninvite, toggleUninvite] = useToggle(false)
  const [openSetWelcomeSpaceAccess, toggleSetWelcomeSpaceAccess] = useToggle(false)
  const [openActivate, toggleActivate] = useToggle(false)
  const { shouldShowHint, onCloseHint } = useShowBecomeHint()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data, loading: loadingQuery } = useQuery(ActionsDocument, {
    variables: { ids: [employee.id] },
  })

  const onClickTerminate = () => {
    const legalEntity = employeeLegalEntity(employee)
    track(trackingEvents.employee.termination.terminateStart, {
      company_id: legalEntity.attributes.company_id,
      user_id: employee.id,
      user_role: employeeJob(contractVersions, employee),
      time_stamp: Date.now(),
    })
    navigate(navigation.employees.id.terminate.fullPath({ id: employee.id }))
  }

  const options = useOptions({
    employee,
    onClickUninvite: toggleUninvite(true),
    onClickBecome: () => {
      track(trackingEvents.employee.become.startBecome, { from: pathname })
      onCloseHint()
      setOpenBecome(true)
    },
    onClickDisableMfa: () => {
      setOpenDisableMfa(true)
    },
    onClickTerminate: () => onClickTerminate(),
    onClickSetWelcomeSpaceAccess: toggleSetWelcomeSpaceAccess(true),
    onClickActivate: toggleActivate(true),
    queryData: data,
  })

  if (!options.length || loadingQuery) return null

  const user = employeeUser(employee)

  return (
    <>
      <BecomeHint onClose={onCloseHint} open={shouldShowHint}>
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button type="secondary" onClick={onCloseHint} iconName="ellipsis" />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="center">
            {options.map(({ title, description, onClick, danger }, index) => (
              <PopoverMenu.TouchableOption
                key={index}
                danger={danger}
                title={title}
                onClick={onClick}
                description={description}
              />
            ))}
          </PopoverMenu.Content>
        </PopoverMenu>
      </BecomeHint>
      {openUninvite && <UninviteModal employee={employee} onClose={toggleUninvite(false)} />}
      {openBecome && (
        <BecomeModal
          accessId={user.id}
          firstName={user.firstName}
          onClose={() => setOpenBecome(false)}
        />
      )}
      {openDisableMfa && (
        <DisableMfaModal employee={employee} onClose={() => setOpenDisableMfa(false)} />
      )}
      {openSetWelcomeSpaceAccess && (
        <SetWelcomeSpaceAccessModal
          employee={employee}
          onClose={toggleSetWelcomeSpaceAccess(false)}
        />
      )}
      {openActivate && <ActivateModal employee={employee} onClose={toggleActivate(false)} />}
    </>
  )
}

export default observer(Actions)
